// The CAROUSEL edit bottom-sheet body — the mobile counterpart of the desktop SlideFilmstripDock.
// A touch slide strip (select / add / duplicate / reorder / delete / Ask-AI per slide) sits on top;
// the ACTIVE slide is edited below through the SAME MobileCanvasEditor used for single-image posts
// (it reads useComposerEditor(), which the CarouselProviderInner points at the active slide). So
// every per-slide layer tool works unchanged, and switching slides just swaps the active canvas.
// Slide management is always-visible touch controls (no hover — mobile has none). Returns null
// without a deck. The sheet shell (MobileBottomSheet) supplies the header/close + "Ask Taka" footer.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Copy, Plus, Sparkles, Trash2 } from 'lucide-react'
import { cn } from 'lib/utils'
import { CompositionCanvas } from '../CompositionCanvas'
import type { PostComposition, SlideRole } from '../types'
import { MobileCanvasEditor } from './MobileCanvasEditor'
import { useCarouselDeck } from './CompositionEditorProvider'

const THUMB_H = 64

const ROLE_LABEL: Record<SlideRole, string> = {
  hook: 'Hook',
  rehook: 'Hook 2',
  body: 'Body',
  cta: 'CTA',
}

export function MobileCarouselEditor({
  onAskAISlide,
}: {
  /** Pin a per-slide chat context ("Slide N of M") so an AI revise targets THIS slide. The
   *  slideCount comes from the live deck (not the lagging rendered masters). */
  onAskAISlide?: (index: number, slideCount: number) => void
}) {
  const deck = useCarouselDeck()
  if (!deck) return null
  return (
    <div className="flex h-full flex-col overflow-hidden bg-bg-2">
      {/* Pick + manage the slide up top; edit it below. */}
      <SlideStrip onAskAISlide={onAskAISlide} />
      {/* The active slide, edited with the SAME touch canvas + layer toolbar as a single image. */}
      <div className="relative min-h-0 flex-1">
        <MobileCanvasEditor />
      </div>
    </div>
  )
}

function SlideStrip({
  onAskAISlide,
}: {
  onAskAISlide?: (index: number, slideCount: number) => void
}) {
  const deck = useCarouselDeck()
  // Two-step delete: first tap arms ("Sure?"), second (within 3s) removes — a stray tap never
  // silently drops a slide.
  const [armedDelete, setArmedDelete] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )
  // Disarm the delete when the active slide changes, so an armed "Sure?" can't carry over.
  const activeIndex = deck?.activeIndex ?? 0
  useEffect(() => setArmedDelete(false), [activeIndex])
  if (!deck) return null

  const onDelete = () => {
    if (!armedDelete) {
      setArmedDelete(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setArmedDelete(false), 3000)
      return
    }
    if (timer.current) clearTimeout(timer.current)
    setArmedDelete(false)
    deck.deleteSlide(activeIndex)
  }

  const count = deck.slides.length

  return (
    <div className="flex-shrink-0 border-b border-line bg-bg-2">
      {/* Thumbnails — tap to make a slide active. */}
      <div className="flex items-center gap-2 overflow-x-auto px-3 py-2">
        {deck.slides.map((slide, i) => (
          <SlideThumb
            key={slide.slide?.id ?? i}
            slide={slide}
            index={i}
            active={i === activeIndex}
            onSelect={() => deck.selectSlide(i)}
          />
        ))}
        {deck.canAdd && (
          <button
            type="button"
            onClick={deck.addSlide}
            aria-label="Add slide"
            className="flex shrink-0 flex-col items-center justify-center gap-1 rounded-[10px] border border-dashed border-line-bright text-paper-dim active:bg-bg-3"
            style={{ height: THUMB_H, width: THUMB_H * 0.8 }}
          >
            <Plus size={16} />
            <span className="text-[10px] font-medium">Add</span>
          </button>
        )}
      </div>

      {/* Management for the ACTIVE slide — always visible (touch has no hover). */}
      <div className="flex items-center gap-1 overflow-x-auto border-t border-line/60 px-2 py-1.5">
        <span className="mr-1 flex-shrink-0 px-1 text-[11px] font-medium text-paper-dim">
          Slide {activeIndex + 1}/{count}
        </span>
        {onAskAISlide && (
          <ManageBtn label="Ask Taka" onClick={() => onAskAISlide(activeIndex, count)}>
            <Sparkles size={14} />
          </ManageBtn>
        )}
        <ManageBtn
          label="Move left"
          disabled={activeIndex === 0}
          onClick={() => deck.moveSlide(activeIndex, activeIndex - 1)}
        >
          <ChevronLeft size={15} />
        </ManageBtn>
        <ManageBtn
          label="Move right"
          disabled={activeIndex >= count - 1}
          onClick={() => deck.moveSlide(activeIndex, activeIndex + 1)}
        >
          <ChevronRight size={15} />
        </ManageBtn>
        {deck.canAdd && (
          <ManageBtn label="Duplicate" onClick={() => deck.duplicateSlide(activeIndex)}>
            <Copy size={13} />
          </ManageBtn>
        )}
        {deck.canDelete && (
          <ManageBtn label={armedDelete ? 'Sure?' : 'Delete'} danger onClick={onDelete}>
            <Trash2 size={13} />
          </ManageBtn>
        )}
      </div>
    </div>
  )
}

function SlideThumb({
  slide,
  index,
  active,
  onSelect,
}: {
  slide: PostComposition
  index: number
  active: boolean
  onSelect: () => void
}) {
  const role = slide.slide?.role
  const width = THUMB_H * (slide.canvas.width / slide.canvas.height)
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Slide ${index + 1}${role ? ` (${ROLE_LABEL[role]})` : ''}`}
      aria-current={active}
      className={cn(
        'relative block flex-shrink-0 overflow-hidden rounded-[10px] border transition-all',
        active ? 'border-acid ring-2 ring-acid/40' : 'border-line'
      )}
      style={{ height: THUMB_H, width }}
    >
      <CompositionCanvas composition={slide} maxHeight={THUMB_H} />
      <span className="absolute top-1 left-1 rounded bg-black/60 px-1 text-[9px] leading-tight font-semibold text-paper">
        {index + 1}
      </span>
      {role && (
        <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[8px] leading-tight font-medium tracking-wide text-paper-dim uppercase">
          {ROLE_LABEL[role]}
        </span>
      )}
    </button>
  )
}

function ManageBtn({
  label,
  onClick,
  danger,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex h-9 min-w-[48px] flex-shrink-0 items-center justify-center gap-1 rounded-[10px] px-2 text-[11px] font-medium transition-colors disabled:opacity-30',
        danger ? 'text-red-300 active:bg-bg-3' : 'text-paper-dim active:bg-bg-3'
      )}
    >
      {children}
      <span>{label}</span>
    </button>
  )
}
